import React, { useState } from 'react'
import {
  Stack,
  Heading,
  Text,
  Button,
  Badge,
  Icon,
  ConfirmButton,
  UserChip,
  Card,
  SimpleMenu
} from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'
import { FaTrash, FaChevronDown, FaDownload } from 'react-icons/fa'

export function meetingEnded (meeting) {
  if (!meeting?.endDate) return false
  return new Date(meeting.endDate) < new Date()
}

function MeetingSessions ({ trialId, participantId, meetingId, sessions }) {
  const [downloadingId, setDownloadingId] = useState(null)

  if (!sessions?.length) return null

  function downloadTranscription (sessionId) {
    setDownloadingId(sessionId)
    api.participants.getTranscriptionLink(trialId, participantId, meetingId, sessionId, ({ url }) => {
      setDownloadingId(null)
      window.open(url, '_blank', 'noopener,noreferrer')
    }, (err) => {
      setDownloadingId(null)
      utils.toast('error', 'Unable to get download link', err.message)
    })
  }

  const menuItems = sessions.map((s) => {
    const label = s.startedAt
      ? utils.formatDateTime(s.startedAt)
      : `Session ${s._id?.slice(-6) || ''}`
    const minuteStr = s.participantMinutes != null
      ? ` (${s.participantMinutes.toFixed(1)} min)`
      : ''
    if (s.transcription?.transcriptionId) {
      return {
        text: `${label}${minuteStr} — Download transcript`,
        icon: FaDownload,
        value: s._id,
        onClick: () => downloadTranscription(s._id)
      }
    }
    return {
      text: `${label}${minuteStr}`,
      onClick: () => {}
    }
  })

  return (
    <SimpleMenu
      trigger={
        <Button size='sm' variant='outline' loading={!!downloadingId}>
          Transcriptions ({sessions.length}) <Icon as={FaChevronDown} ml={1} />
        </Button>
      }
      items={menuItems}
    />
  )
}

/**
 * Video meeting summary: schedule, personnel, participant link, join-as-host, transcriptions, edit/delete.
 * Used on the Meetings tab and in calendar detail modals.
 */
export default function ParticipantMeetingCard ({
  meeting,
  trialId,
  participantId,
  onJoinHost,
  onEdit,
  onDelete,
  deletingId
}) {
  const { user, trial, getTrialUsers } = useStore()
  const trialUsers = getTrialUsers(trialId)
  const ended = meetingEnded(meeting)

  return (
    <Card>
      <Stack gap={4}>
        <Heading size='sm'>{meeting.name || 'Meeting'}</Heading>
        <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={2}>
          <Badge
            size='sm'
            colorPalette={ended ? 'gray' : 'green'}
            variant='subtle'
          >
            {ended ? 'Ended' : 'Active'}
          </Badge>
          {meeting.startAt &&
            <Stack gap={1} textAlign='right'>
              <Text fontSize='sm'>
                Scheduled for {utils.formatDateTime(meeting.startAt)}
              </Text>
              {meeting.endDate &&
                <Text fontSize='xs' color='fg.subtle'>
                  Room valid until {utils.formatDateTime(meeting.endDate)}
                </Text>}
            </Stack>}
        </Stack>

        {(meeting.personnel?.length > 0) && (
          <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
            {trialUsers
              .filter((u) => (meeting.personnel || []).includes(u._id))
              .map((u) => (
                <UserChip key={u._id} user={u} compact />
              ))}
          </Stack>
        )}
        <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
          {!ended && meeting.roomUrl && (
            <Button
              size='sm'
              onClick={() => {
                utils.copyToClipboard(`${window.location.origin}/meetings/${meeting._id}?password=${meeting.password}`)
                utils.toast('success', 'Participant link copied')
              }}
            >
              Copy participant link
            </Button>
          )}
          {!ended && meeting.hostRoomUrl && utils.canJoinParticipantMeetings(user, trial) && (
            <Button
              size='sm'
              colorPalette='primary'
              onClick={() => onJoinHost?.(meeting)}
            >
              Join as host
            </Button>
          )}
          {meeting.sessions?.length > 0 && (
            <MeetingSessions
              trialId={trialId}
              participantId={participantId}
              meetingId={meeting._id}
              sessions={meeting.sessions}
            />
          )}
        </Stack>
        {(onEdit || onDelete) && (
          <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
            {utils.canCreateParticipantMeetings(user, trial) && onEdit && (
              <Button size='sm' variant='outline' onClick={() => onEdit(meeting)}>
                Edit
              </Button>
            )}
            {utils.canDeleteParticipantMeetings(user, trial) && onDelete &&
              <ConfirmButton
                trigger={
                  <Button
                    size='sm'
                    variant='ghost'
                    isLoading={deletingId === meeting._id}
                  >
                    <Icon as={FaTrash} />
                  </Button>
                }
                header='Delete meeting?'
                body='The room link will stop working. This cannot be undone.'
                onConfirm={() => onDelete(meeting._id)}
                actionColor='red'
              />}
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
